import { useEffect, useRef } from 'react';
import { inTvApp } from '../tv/bridge';

/**
 * Display lock: once a screen is paired, the display can't be left or changed from the screen itself. Clicks,
 * dragging, right-click, text selection, zooming, the keyboard shortcuts a page can catch, going back, and closing
 * or reloading by accident are all blocked, and it goes straight back to full screen if anything takes it out.
 * Only unpairing the screen from the dashboard or the iPhone app releases it (the display then shows its pairing
 * code again).
 *
 * A web page can't stop everything (a browser always lets Esc leave full screen, and the TV's own casting and menus
 * are outside the page). Where the browser supports it, Esc is captured too, so it has to be held down. For a display
 * nobody can leave at all, run it in the NoorView display app or a browser in kiosk mode (see SETUP.md).
 */
export function useDisplayLock(on: boolean, o: { onInput?: () => void; allow?: boolean } = {}) {
  // Any touch, click or key press is reported (it opens the exit PIN pad). While `allow` is set (the pad is open),
  // input reaches the page as normal.
  const cfg = useRef(o);
  cfg.current = o;
  useEffect(() => {
    if (!on) return;
    const doc = document.documentElement;
    document.body.classList.add('display-locked');
    const stop = (e: Event) => { e.preventDefault(); e.stopPropagation(); };
    const nav = navigator as Navigator & { keyboard?: { lock?: (keys?: string[]) => Promise<void>; unlock?: () => void }; wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> } };
    let wake: { release: () => Promise<void> } | null = null;
    const keepAwake = () => { if (document.visibilityState === 'visible') void nav.wakeLock?.request('screen').then((w) => { wake = w; }).catch(() => undefined); };

    // Back into full screen at the next touch, click or key press (browsers only allow it then).
    const full = () => {
      if (!document.fullscreenElement) void doc.requestFullscreen?.({ navigationUI: 'hide' } as FullscreenOptions).then(() => nav.keyboard?.lock?.(['Escape'])).catch(() => undefined);
      else void nav.keyboard?.lock?.(['Escape'])?.catch?.(() => undefined);
    };
    const allowed = () => !!cfg.current.allow;
    const guard = (e: Event) => { if (!allowed()) stop(e); };
    const onPointer = (e: Event) => { if (!allowed()) cfg.current.onInput?.(); guard(e); full(); };
    const onKey = (e: KeyboardEvent) => { if (!allowed() && !e.repeat) cfg.current.onInput?.(); guard(e); if (!allowed()) full(); };
    const onWheel = (e: WheelEvent) => { if (e.ctrlKey || e.metaKey) e.preventDefault(); };
    const onUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    // The back button (or a back swipe) stays on this page.
    history.pushState({ nvLocked: true }, '');
    const onPop = () => history.pushState({ nvLocked: true }, '');
    const onVisible = () => keepAwake();
    const onFull = () => { if (!document.fullscreenElement) nav.keyboard?.unlock?.(); };

    const opts = { capture: true, passive: false } as const;
    const events: Array<[string, EventListener]> = [
      ['contextmenu', stop], ['selectstart', guard], ['dragstart', stop], ['dblclick', stop], ['auxclick', stop],
      ['pointerdown', onPointer], ['mousedown', guard], ['click', guard], ['touchstart', guard], ['touchmove', stop], ['gesturestart', stop],
      ['keydown', onKey as EventListener], ['keyup', guard], ['wheel', onWheel as EventListener],
    ];
    for (const [t, f] of events) window.addEventListener(t, f, opts);
    // In a browser, closing or reloading by accident asks first. Not in the NoorView apps: they keep the screen open
    // themselves, and the question would stop them closing it after the exit PIN (and loading a new version).
    if (!inTvApp) window.addEventListener('beforeunload', onUnload);
    window.addEventListener('popstate', onPop);
    document.addEventListener('visibilitychange', onVisible);
    document.addEventListener('fullscreenchange', onFull);
    keepAwake();
    full();

    return () => {
      for (const [t, f] of events) window.removeEventListener(t, f, opts);
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('popstate', onPop);
      document.removeEventListener('visibilitychange', onVisible);
      document.removeEventListener('fullscreenchange', onFull);
      document.body.classList.remove('display-locked');
      nav.keyboard?.unlock?.();
      void wake?.release().catch(() => undefined);
    };
  }, [on]);
}
